import {
  Outlet,
  createRouter,
  createRoute,
  createRootRoute,
  useLocation,
} from '@tanstack/react-router'
import { SidebarProvider } from '@uavity-core/ui/components/sidebar'
import { AppSidebar } from './components/app-sidebar'
import { PublicAppFrame } from './components/public-app-frame'
import { ThemeProvider } from './components/theme-provider'
import { ClerkProvider, SignedIn, SignedOut } from '@clerk/clerk-react'
import { JSX, useEffect } from 'react'
import { RealtimeProvider } from './api/v1/realtime'
import { useStore } from './store/store'
import {
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { NuqsAdapter } from 'nuqs/adapters/react'
import { Clerk } from '@clerk/clerk-js'
import { Toaster } from '@uavity-core/ui/components/sonner'
import { RouteMetadata } from './components/seo/RouteMetadata'
import { PerformanceMonitor } from './components/seo/PerformanceMonitor'
import {
  AccountPage,
  FlightPlanDetailRoute,
  FlightPlanSubmissionRoute,
  FlightPlansRoute,
  FlightRestrictionZoneDetailRoute,
  FlightRestrictionZoneMapRoute,
  FlightRestrictionZoneViolationsRoute,
  FlightRestrictionZonesRoute,
  GeofenceCreateRoute,
  GeofenceDetailRoute,
  GeofenceEditRoute,
  GeofenceViolationsRoute,
  GeofencesRoute,
  LiveDetectionsRoute,
  LoginPage,
  MapRoute,
  PreferencesRoute,
  PrivacyPolicyRoute,
  SubscriptionsRoute,
  SupportPage,
  TermsOfUseRoute,
} from './lazy-route-components'

export const clerkClient = new Clerk(import.meta.env.VITE_CLERK_PUBLISHABLE_KEY)

const AuthenticatedLayout = ({ children }: { children: JSX.Element }) => {
  const store = useStore()
  useEffect(() => {
    const loadClerk = async () => {
      await clerkClient.load()
    }

    const intervalId = setInterval(() => {
      store.reset()
    }, 1000 * 30)

    loadClerk()
    return () => clearInterval(intervalId)
  }, [store])

  return (
    <>
      <SignedIn>
        <ThemeProvider defaultTheme="dark" storageKey="stratos-ui-theme">
          <RealtimeProvider>
            <div className="relative min-h-[100dvh] bg-background">
              <SidebarProvider>
                <AppSidebar variant="sidebar" className="z-20" />
                <PublicAppFrame>{children}</PublicAppFrame>
              </SidebarProvider>
            </div>
          </RealtimeProvider>
        </ThemeProvider>
      </SignedIn>
      <SignedOut>
        <LoginPage />
      </SignedOut>
    </>
  )
}

const BaseLayout = () => {
  const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY
  const location = useLocation()

  if (!PUBLISHABLE_KEY) {
    throw new Error("Missing clerk Publishable Key")
  }

  const queryClient = new QueryClient()

  // Routes that don't require authentication
  const publicRoutes = ['/legal/privacy-policy', '/legal/terms-of-use']
  const isPublicRoute = publicRoutes.includes(location.pathname)

  if (isPublicRoute) {
    return (
      <ClerkProvider publishableKey={PUBLISHABLE_KEY}>
        <Toaster />
        <QueryClientProvider client={queryClient}>
          <NuqsAdapter>
            <ThemeProvider defaultTheme="dark" storageKey="stratos-ui-theme">
              <div className="relative min-h-screen bg-background">
                <main className="bg-background">
                  <PerformanceMonitor />
                  <Outlet />
                </main>
              </div>
            </ThemeProvider>
          </NuqsAdapter>
        </QueryClientProvider>
      </ClerkProvider>
    )
  }

  return (
    <ClerkProvider publishableKey={PUBLISHABLE_KEY} >
      <Toaster />
      <QueryClientProvider client={queryClient}>
        <NuqsAdapter>
          <AuthenticatedLayout>
              <Outlet />
          </AuthenticatedLayout>
        </NuqsAdapter>
      </QueryClientProvider>
    </ClerkProvider>
  )
}

const rootRoute = createRootRoute({
  component: BaseLayout,
})

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: () => (
    <>
      <RouteMetadata
        title="Uavity Map - Real-time Airspace Tracking"
        description="View real-time Airspace locations and flight paths on an interactive map"
      />
      <MapRoute />
    </>
  ),
})

const mapRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/map',
  component: () => (
    <>
      <RouteMetadata
        title="Uavity Map - Real-time Airspace Tracking"
        description="View real-time Airspace locations and flight paths on an interactive map"
      />
      <MapRoute />
    </>
  ),
})

const liveDetectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/live-detections',
  component: () => (
    <>
      <RouteMetadata
        title="Uavity Live Detections - Sensor Network"
        description="Monitor realtime Remote ID detections from accessible Uavity sensors."
      />
      <LiveDetectionsRoute />
    </>
  ),
})

const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account',
  component: AccountPage,
})

const preferencesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/preferences',
  component: PreferencesRoute,
})

// Geofence routes
const geofencesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/geofences',
  component: () => (
    <>
      <RouteMetadata
        title="Geofencing - Uavity Airspace Management"
        description="Create and manage custom geofences for airspace monitoring. Set up virtual boundaries and automated alerts for drone activity."
        keywords="geofencing, virtual boundaries, drone monitoring, airspace alerts, automated detection, boundary management"
        tags={['geofencing', 'virtual boundaries', 'automated alerts', 'boundary management', 'drone detection']}
      />
      <GeofencesRoute />
    </>
  ),
})

const geofenceCreateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/geofences/create',
  component: () => (
    <>
      <RouteMetadata
        title="Create Geofence - Uavity Airspace Management"
        description="Create a new geofence for airspace monitoring. Define virtual boundaries and configure automated alerts for drone activity."
        keywords="create geofence, new boundary, virtual fence setup, drone monitoring boundary, automated alerts configuration"
        tags={['geofence creation', 'boundary setup', 'alert configuration', 'monitoring setup']}
      />
      <GeofenceCreateRoute />
    </>
  ),
})

const geofenceDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/geofences/$geofenceId',
  component: () => (
    <>
      <RouteMetadata
        title="Geofence Details - Uavity Airspace Management"
        description="View detailed information about a geofence including boundaries, monitoring status, and violation history."
        keywords="geofence details, boundary information, monitoring status, violation history, geofence analytics"
        tags={['geofence details', 'monitoring status', 'violation tracking', 'boundary information']}
      />
      <GeofenceDetailRoute />
    </>
  ),
})

const geofenceEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/geofences/$geofenceId/edit',
  component: () => (
    <>
      <RouteMetadata
        title="Edit Geofence - Uavity Airspace Management"
        description="Edit geofence settings including boundaries, alert configurations, and monitoring parameters."
        keywords="edit geofence, modify boundary, geofence configuration, alert settings, monitoring parameters"
        tags={['geofence editing', 'configuration update', 'boundary modification', 'settings management']}
      />
      <GeofenceEditRoute />
    </>
  ),
})

const geofenceViolationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/geofences/$geofenceId/violations',
  component: () => (
    <>
      <RouteMetadata
        title="Geofence Violations - Uavity Airspace Management"
        description="View and manage geofence violations. Track unauthorized drone activity and review incident reports."
        keywords="geofence violations, unauthorized activity, incident reports, violation tracking, security alerts"
        tags={['violations', 'incident tracking', 'security alerts', 'unauthorized activity']}
      />
      <GeofenceViolationsRoute />
    </>
  ),
})

const flightRestrictionZonesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/flight-restriction-zones',
  component: () => (
    <>
      <RouteMetadata
        title="Flight Restriction Zones - Uavity Airspace Management"
        description="Monitor and manage flight restriction zones across your area"
      />
      <FlightRestrictionZonesRoute />
    </>
  ),
})

const flightRestrictionZoneDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/flight-restriction-zones/$zoneSlug',
  component: () => (
    <>
      <RouteMetadata
        title="Flight Restriction Zone Details - Uavity Airspace Management"
        description="View detailed information about a specific flight restriction zone"
      />
      <FlightRestrictionZoneDetailRoute />
    </>
  ),
})

const flightRestrictionZoneMapRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/flight-restriction-zones/$zoneSlug/map',
  component: () => (
    <>
      <RouteMetadata
        title="Flight Restriction Zone Map - Uavity Airspace Management"
        description="View flight restriction zone on an interactive map"
      />
      <FlightRestrictionZoneMapRoute />
    </>
  ),
})

const flightRestrictionZoneViolationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/flight-restriction-zones/$zoneSlug/violations',
  component: () => (
    <>
      <RouteMetadata
        title="Flight Restriction Zone Violations - Uavity Airspace Management"
        description="View and manage violations for a specific flight restriction zone"
      />
      <FlightRestrictionZoneViolationsRoute />
    </>
  ),
})

const flightPlanRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/flight-plan/create',
  component: () => (
    <>
      <RouteMetadata
        title="Create Flight Plan - Uavity Airspace Management"
        description="Plan and submit a new flight plan."
        keywords="create flight plan, drone operations, flight submission"
        tags={['flight planning', 'drone operations']}
      />
      <FlightPlanSubmissionRoute />
    </>
  ),
})

const flightPlansRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/flight-plans',
  component: () => (
    <>
      <RouteMetadata
        title="Flight Plans - Uavity Airspace Management"
        description="View and manage your flight plans."
      />
      <FlightPlansRoute />
    </>
  ),
})

const flightPlanDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/flight-plans/$flightPlanId',
  component: () => (
    <>
      <RouteMetadata
        title="Flight Plan Details - Uavity Airspace Management"
        description="View detailed information about a flight plan."
      />
      <FlightPlanDetailRoute />
    </>
  ),
})

const subscriptionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/subscriptions',
  component: SubscriptionsRoute,
})

const privacyPolicyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/legal/privacy-policy',
  component: () => (
    <>
      <RouteMetadata
        title="Privacy Policy - Uavity"
        description="Read our privacy policy to understand how we collect, use, and protect your data."
        keywords="privacy policy, data protection, user privacy, data collection, privacy rights"
        tags={['privacy', 'legal', 'data protection', 'user rights']}
      />
      <PrivacyPolicyRoute />
    </>
  ),
})

const termsOfUseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/legal/terms-of-use',
  component: () => (
    <>
      <RouteMetadata
        title="Terms of Use - Uavity"
        description="Read our terms of use to understand the rules and guidelines for using our services."
        keywords="terms of use, legal terms, user agreement, service terms, terms and conditions"
        tags={['legal', 'terms', 'agreement', 'user terms']}
      />
      <TermsOfUseRoute />
    </>
  ),
})

const supportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/support',
  component: () => (
    <>
      <RouteMetadata
        title="Support - Uavity"
        description="Get help and support for Uavity services. Contact our team directly."
        keywords="support, help, contact, assistance, customer service"
        tags={['support', 'help', 'contact']}
      />
      <SupportPage />
    </>
  ),
})

const routeTree = rootRoute.addChildren([
  indexRoute,
  mapRoute,
  liveDetectionsRoute,
  accountRoute,
  preferencesRoute,
  geofencesRoute,
  geofenceCreateRoute,
  geofenceDetailRoute,
  geofenceEditRoute,
  geofenceViolationsRoute,
  flightRestrictionZonesRoute,
  flightRestrictionZoneDetailRoute,
  flightRestrictionZoneMapRoute,
  flightRestrictionZoneViolationsRoute,
  flightPlansRoute,
  flightPlanRoute,
  flightPlanDetailRoute,
  subscriptionsRoute,
  privacyPolicyRoute,
  termsOfUseRoute,
  supportRoute,
])

function RouterPendingFallback() {
  return (
    <div className="flex min-h-[30vh] items-center justify-center">
      <span className="text-muted-foreground text-sm">Loading…</span>
    </div>
  )
}

export const router = createRouter({
  routeTree,
  defaultPendingComponent: RouterPendingFallback,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
